/* src/hooks/useHorizontalScroll.js */
import { useCallback, useEffect, useRef, useState } from 'react'

const clamp = (v, min, max) => Math.min(Math.max(v, min), max)

/**
 * Перетворює вертикальний скрол на горизонтальний рух треку.
 *
 * КОЛЕСО МИШІ / ТРЕКПАД: один жест = одна панель. Дискретні кроки колеса
 * (~100px) значно менші за «довжину» панелі (~100vw), тож нативний скрол
 * колесом застрягає між панелями, а снеп тягне назад — положення смикається.
 * Тому wheel перехоплюється (preventDefault) і замінюється плавною анімацією
 * до сусідньої панелі. Інерційний хвіст трекпада гаситься кулдауном.
 *
 * СКРОЛБАР / КЛАВІАТУРА / ТАЧ: рухаються нативно; після паузи докручує снеп.
 *
 * ЛОК ПІСЛЯ ПРИЇЗДУ: щойно панель доведена (settled), колесо блокується на
 * panelLock мс — поки секція програє свої вхідні анімації, наступний скрол
 * неможливий. Діє на будь-який спосіб приїзду (колесо, снеп, скролбар).
 *
 * АКТИВНА ПАНЕЛЬ: з прогресу рахуємо, яка панель по центру (active) і чи вона
 * доведена (settled). Стартуємо з settled:false, щоб ПЕРША панель на першому
 * рендері спершу була "armed" (сховано), а тоді стала "active" — інакше вона
 * рендериться одразу в кінцевому стані й анімація входу не програється.
 *
 * @returns {{ sectionRef, trackRef, active: number, settled: boolean }}
 */
export function useHorizontalScroll({
                                      enabled,
                                      snap = true,
                                      snapDelay = 140,
                                      snapDuration = 550,
                                      wheelDuration = 700,   // тривалість перегортання панелі колесом
                                      wheelThreshold = 40,   // сумарний deltaY (px), що вважається свідомим жестом
                                      wheelCooldown = 250,   // мс після анімації, поки ковтаємо інерційний хвіст
                                      panelLock = 700,      // мс після доведення панелі: чекаємо її вхідні анімації
                                                             // (найдовший ланцюжок: етап 3 = 1.6×900мс + 900мс ≈ 2340мс)
                                    }) {
  const sectionRef = useRef(null)
  const trackRef = useRef(null)
  const rafRef = useRef(0)
  const distanceRef = useRef(0)
  const panelsRef = useRef(0)
  const idleRef = useRef(0)
  const snappingRef = useRef(false)
  const wheelRef = useRef({ acc: 0, lastTime: 0, cooldownUntil: 0 })

  const activeRef = useRef(0)
  const settledRef = useRef(false)
  const [panel, setPanel] = useState({ active: 0, settled: false })

  const measure = useCallback(() => {
    const section = sectionRef.current
    const track = trackRef.current
    if (!section || !track) return
    const distance = Math.max(0, track.scrollWidth - window.innerWidth)
    distanceRef.current = distance
    panelsRef.current = track.children.length
    section.style.height = `${window.innerHeight + distance}px`
  }, [])

  const update = useCallback(() => {
    const section = sectionRef.current
    const track = trackRef.current
    if (!section || !track) return
    const rect = section.getBoundingClientRect()
    const scrollable = section.offsetHeight - window.innerHeight
    const progress = scrollable > 0 ? clamp(-rect.top / scrollable, 0, 1) : 0
    track.style.transform = `translate3d(${-progress * distanceRef.current}px, 0, 0)`

    const panels = panelsRef.current
    if (panels > 1) {
      const frac = progress * (panels - 1)
      const idx = clamp(Math.round(frac), 0, panels - 1)
      const isSettled = Math.abs(frac - idx) < 0.12
      // свіже доведення панелі → лок колеса на час її вхідних анімацій
      if (idx !== activeRef.current) {
        activeRef.current = idx
        settledRef.current = isSettled
        setPanel({ active: idx, settled: isSettled })
        if (isSettled) wheelRef.current.cooldownUntil = performance.now() + panelLock
      } else if (isSettled && !settledRef.current) {
        settledRef.current = true
        setPanel({ active: idx, settled: true })
        wheelRef.current.cooldownUntil = performance.now() + panelLock
      }
    }
  }, [panelLock])

  /* Плавний скрол вікна до targetY — єдина реалізація для снепу й колеса.
     Поки триває, snappingRef блокує idle-снеп і нові wheel-жести. */
  const smoothScrollTo = useCallback((targetY, duration) => {
    const startY = window.scrollY
    const delta = targetY - startY
    if (Math.abs(delta) < 1) return

    const easeInOutCubic = (t) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

    const start = performance.now()
    snappingRef.current = true
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration)
      window.scrollTo(0, startY + delta * easeInOutCubic(t))
      if (t < 1 && snappingRef.current) requestAnimationFrame(tick)
      else {
        snappingRef.current = false
        // не коротшаємо довший panelLock, виставлений при доведенні панелі
        wheelRef.current.cooldownUntil = Math.max(
          wheelRef.current.cooldownUntil,
          performance.now() + wheelCooldown,
        )
      }
    }
    requestAnimationFrame(tick)
  }, [wheelCooldown])

  const scrollToPanel = useCallback((idx, duration) => {
    const section = sectionRef.current
    if (!section) return
    const panels = panelsRef.current
    const scrollable = section.offsetHeight - window.innerHeight
    if (panels <= 1 || scrollable <= 0) return

    const target = clamp(idx, 0, panels - 1)
    const sectionTop = section.getBoundingClientRect().top + window.scrollY
    smoothScrollTo(sectionTop + (target / (panels - 1)) * scrollable, duration)
  }, [smoothScrollTo])

  const snapToNearest = useCallback(() => {
    const section = sectionRef.current
    if (!section) return
    const panels = panelsRef.current
    const scrollable = section.offsetHeight - window.innerHeight
    if (panels <= 1 || scrollable <= 0) return

    const rect = section.getBoundingClientRect()
    const progress = clamp(-rect.top / scrollable, 0, 1)
    if (progress <= 0 || progress >= 1) return

    scrollToPanel(Math.round(progress * (panels - 1)), snapDuration)
  }, [scrollToPanel, snapDuration])

  const scheduleSnap = useCallback(() => {
    window.clearTimeout(idleRef.current)
    idleRef.current = window.setTimeout(snapToNearest, snapDelay)
  }, [snapToNearest, snapDelay])

  const onScroll = useCallback(() => {
    cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(update)
    if (snap && !snappingRef.current) scheduleSnap()
  }, [update, snap, scheduleSnap])

  /* Колесо миші / трекпад: перегортання панелей по одній за жест */
  const onWheel = useCallback((e) => {
    const section = sectionRef.current
    if (!section || e.ctrlKey) return          // ctrl+wheel — зум браузера

    // хайджекаємо тільки коли sticky-в'юпорт секції реально закриває екран
    const rect = section.getBoundingClientRect()
    if (rect.top > 0 || rect.bottom < window.innerHeight) return

    const panels = panelsRef.current
    const scrollable = section.offsetHeight - window.innerHeight
    if (panels <= 1 || scrollable <= 0) return

    const dir = Math.sign(e.deltaY)
    if (!dir) return

    // на краях віддаємо жест браузеру — раптом над/під секцією є контент
    const progress = clamp(-rect.top / scrollable, 0, 1)
    if ((dir < 0 && progress <= 0) || (dir > 0 && progress >= 1)) return

    e.preventDefault()

    const now = performance.now()
    const w = wheelRef.current
    // триває анімація або ще котиться інерція попереднього жесту
    if (snappingRef.current || now < w.cooldownUntil) return

    // пауза > 200мс — новий жест: старий накопичений хвіст неактуальний
    if (now - w.lastTime > 200) w.acc = 0
    w.lastTime = now

    // нормалізуємо режим дельт (рядки/сторінки → пікселі)
    const step = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? window.innerHeight : 1
    w.acc += e.deltaY * step

    if (Math.abs(w.acc) < wheelThreshold) return
    w.acc = 0
    scrollToPanel(activeRef.current + dir, wheelDuration)
  }, [scrollToPanel, wheelDuration, wheelThreshold])

  /* Тач/клавіатура перебивають анімацію, що триває, — юзер головніший */
  const cancelSnap = useCallback(() => {
    snappingRef.current = false
  }, [])

  useEffect(() => {
    const section = sectionRef.current
    const track = trackRef.current
    if (!section || !track) return

    if (!enabled) {
      section.style.height = ''
      track.style.transform = ''
      return
    }

    measure()
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', measure)
    window.addEventListener('wheel', onWheel, { passive: false })
    window.addEventListener('touchstart', cancelSnap, { passive: true })
    window.addEventListener('keydown', cancelSnap)
    const settle = window.setTimeout(measure, 250)
    document.fonts?.ready?.then(measure).catch(() => {})

    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', measure)
      window.removeEventListener('wheel', onWheel)
      window.removeEventListener('touchstart', cancelSnap)
      window.removeEventListener('keydown', cancelSnap)
      cancelAnimationFrame(rafRef.current)
      window.clearTimeout(settle)
      window.clearTimeout(idleRef.current)
      snappingRef.current = false
    }
  }, [enabled, measure, update, onScroll, onWheel, cancelSnap])

  return { sectionRef, trackRef, active: panel.active, settled: panel.settled }
}
